import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import {
  ICorrelationCurveChartData,
  ICorrelationCurveChartDataPoint,
} from '@app/api/interfaces/chart-data/correlation-curve';
import { CorrelationsMockService } from '@mock/data-services/correlations-mock.service';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';

@Injectable({ providedIn: 'root' })
export class CorrelationCurveMockService extends MockService {
  private correlationsMockService = inject(CorrelationsMockService);

  public get(projectId: string, correlationId: string): Observable<IApiResponse<ICorrelationCurveChartData, object>> {
    const correlation = this.correlationsMockService.correlations.find(el => el.id === correlationId);

    const values: ICorrelationCurveChartDataPoint[] = correlation
      ? this.generateAccordingToCorrelation(correlation)
      : [];

    const data = {
      values,
    };

    return this.mockResponse(
      'CORRELATION_CURVE:get',
      {
        data,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateAccordingToCorrelation(corelation: ICorrelation): ICorrelationCurveChartDataPoint[] {
    const count = convertStringInToSumOfAllCharacters(corelation.id) % 500 || 100;
    const config = {
      steps: [0.5, 0.6, 0.7, 0.8, 0.9, 1],
      timeSteps: [0.05, 0.06, 0.07, 0.08, 0.09, 0.1],
    };

    const step = config.steps[count % config.steps.length];
    const timeStep = config.timeSteps[count % config.timeSteps.length];
    const curves: ICorrelationCurveChartDataPoint[] = [];

    const offset = Math.floor(count / 2);

    for (let i = 0; i < count; i++) {
      const positionBacklash = Math.sin(i * 1.5 + count) * (step * 0.15);
      const position = (i - offset) * step + positionBacklash;

      const time = i * timeStep;

      const toothDirection = i % 2 === 0 ? 1 : -1;
      const jitter = toothDirection * (0.1 + Math.abs(Math.sin(i)) * 0.2);

      const baseValue = 0.4 + Math.abs(Math.cos(i * 0.5 + count)) * 0.5;
      let normalizedValue = baseValue + jitter;

      const spikeInterval = 35;
      if ((i + (count % 13)) % spikeInterval === 0) {
        normalizedValue = 3.0 + Math.abs(Math.sin(i + count)) * 1.0;
      }

      normalizedValue = Math.min(Math.max(normalizedValue, 0.05), 4);

      curves.push({
        position: Number(position.toFixed(3)),
        time: Number(time.toFixed(2)),
        normalizedValue: Number(normalizedValue.toFixed(3)),
      });
    }

    return curves;
  }
}
